/**
 * CapacitacionView -- Learning System admin: questions bank, progress dashboard.
 * Uses NxListViewer for tables, LearningApiClient for data.
 * Artifact version: v1.0 (aligned with standard v1)
 *
 * @module presentation/views/CapacitacionView
 */

import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';
import { renderNxList, bindNxListEvents, NX_LIST_VIEWER_VERSION } from '../shared/NxListViewer.js';
import { buildLearningEngine, NX_LEARNING_ENGINE_VERSION } from '../shared/NxLearningEngine.js';
import { buildFlashCards, NX_FLASHCARDS_VERSION } from '../shared/NxFlashCards.js';
import { useFeatureGate } from '../hooks/useFeatureGate.js';
import { renderFeatureGate } from '../components/FeatureGate.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';

// Deuda 4: el escape local solo cambiaba "<" y este archivo lo usa dentro de atributos con comilla doble.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
let currentTab = 'questions';

export async function renderCapacitacion(container) {
  // PANEL-SUBS-FEATURES-001 T3 — gate por feature_key 'capacitacion'
  const businessId = getSelectedBusiness();
  const gate = await useFeatureGate(businessId, 'capacitacion');
  if (gate.blocked) {
    renderFeatureGate(container, gate, { feature: 'capacitacion', viewName: 'Capacitacion' });
    return;
  }

  currentTab = 'cursos';

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Capacitacion</h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">NxLearningEngine v${NX_LEARNING_ENGINE_VERSION} &middot; NxFlashCards v${NX_FLASHCARDS_VERSION} &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}</p>
        </div>
      </div>

      <div class="task-filter-bar" id="nx-cap-tabs">
        <button class="btn-filter active" data-cap-tab="cursos">Cursos</button>
        <button class="btn-filter" data-cap-tab="questions">Banco de Preguntas</button>
        <button class="btn-filter" data-cap-tab="progress">Progreso del Equipo</button>
        <button class="btn-filter" data-cap-tab="flashcards">Tarjetas</button>
      </div>

      <div id="nx-cap-content" style="margin-top:12px"></div>
    </section>`;

  const $tabs = document.getElementById('nx-cap-tabs');
  const $content = document.getElementById('nx-cap-content');

  $tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $tabs.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTab = btn.dataset.capTab;
    renderTab($content, currentTab);
  });

  renderTab($content, currentTab);
}

async function renderTab($el, tab) {
  if (tab === 'cursos') await renderCursosTab($el);
  else if (tab === 'questions') await renderQuestionsTab($el);
  else if (tab === 'progress') await renderProgressTab($el);
  else if (tab === 'flashcards') await renderFlashCardsTab($el);
  else await renderPreviewTab($el);
}

// CURSOS (SKILL-USO / primer uso de Desktop): selector de curso -> teoria corta
// + quiz del tema con RETROALIMENTACION INMEDIATA. Los de "primer uso" (codigos
// USO-*) van en su propia seccion; dentro de cada seccion, orden ALFABETICO +
// barra de busqueda (feedback Josse 2026-07-24).
async function renderCursosTab($el) {
  $el.innerHTML = '<p class="empty-state">Cargando cursos...</p>';

  // El contenido tiene DOS convenciones de categoria en las preguntas:
  //  - USO (primer uso): category = codigo del tema (USO-T1..USO-T4).
  //  - GOV/SWE/MKT: category = categoria del skill (gobernanza, ingenieria...).
  // El question_count del endpoint /topics solo cubre la segunda -> los cursos
  // USO daban 0 y desaparecian (bug que reporto Josse). Resolvemos client-side:
  // para cada tema, la CLAVE del quiz es su codigo si hay preguntas asi, o la
  // categoria de su skill en su defecto. Una sola pasada, sin tocar el API.
  const [topRes, qRes, skRes] = await Promise.all([
    LearningApiClient.getTopics(),
    LearningApiClient.getQuestions({ limit: 1000, random: false }),
    LearningApiClient.getSkills()
  ]);
  const allTopics = topRes.ok ? (topRes.data?.data || topRes.data || []) : [];
  const allQ = qRes.ok ? (qRes.data?.data || []) : [];
  const allSkills = skRes.ok ? (skRes.data?.data || skRes.data || []) : [];
  const countByCat = {};
  for (const q of allQ) { const c = q.category; if (c) countByCat[c] = (countByCat[c] || 0) + 1; }
  const skillCat = {};
  for (const s of allSkills) { if (s.code) skillCat[s.code] = s.category; }

  const topics = allTopics.map(t => {
    const porCodigo = countByCat[t.code] || 0;
    if (porCodigo > 0) return { ...t, _quizKey: t.code, _count: porCodigo };
    const cat = skillCat[t.skill_code || t.skillCode];
    return { ...t, _quizKey: cat, _count: cat ? (countByCat[cat] || 0) : 0 };
  }).filter(t => t._count > 0);

  if (!topics.length) {
    $el.innerHTML = '<p class="empty-state">Aun no hay cursos con preguntas. Agrega preguntas en el Banco.</p>';
    return;
  }

  const esUso = (t) => /^USO/i.test(t.code || '');
  const alfabetico = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' });
  const primerUso = topics.filter(esUso).sort(alfabetico);
  const otros = topics.filter(t => !esUso(t)).sort(alfabetico);

  const card = (t) => {
    const n = t._count;
    const primero = esUso(t);
    return `
      <button type="button" class="nx-curso-card" data-topic="${escHtml(t.code)}" data-name="${escHtml((t.name || '').toLowerCase())}"
        style="text-align:left;display:block;width:100%;background:#161b22;border:1px solid ${primero ? '#238636' : '#30363d'};border-radius:10px;padding:14px 16px;margin-bottom:10px;cursor:pointer;color:#c9d1d9">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px">
          <strong style="font-size:0.98rem">${escHtml(t.name)}</strong>
          ${primero ? '<span style="font-size:0.875rem;background:#23863622;color:#3fb950;border:1px solid #238636;border-radius:999px;padding:2px 8px;white-space:nowrap">PRIMER USO</span>' : ''}
        </div>
        <div style="font-size:0.875rem;color:#8b949e;margin-top:4px">${n} pregunta${n === 1 ? '' : 's'}${t.applies_to && t.applies_to !== 'all' ? ` &middot; ${escHtml(t.applies_to)}` : ''}</div>
      </button>`;
  };

  $el.innerHTML = `
    <div class="detail-section" id="nx-curso-picker">
      <input type="search" id="nx-curso-buscar" placeholder="Buscar curso por nombre..." class="form-input"
             style="width:100%;margin-bottom:16px" aria-label="Buscar curso"/>
      <div id="nx-curso-lista">
        ${primerUso.length ? `<h2 class="nx-curso-grupo" data-grupo style="margin-bottom:6px">Primer uso de Desktop</h2>
          <p class="nx-curso-grupo" data-grupo style="font-size:0.875rem;color:#8b949e;margin-bottom:12px">Lo esencial para arrancar: crear productos, vender y cerrar la caja. Cada curso da la teoría corta y luego te pregunta con retroalimentación al instante.</p>
          ${primerUso.map(card).join('')}` : ''}
        ${otros.length ? `<h2 class="nx-curso-grupo" data-grupo style="margin:20px 0 12px">Otros temas</h2>${otros.map(card).join('')}` : ''}
      </div>
      <p id="nx-curso-vacio" class="empty-state" hidden>Ningún curso coincide con tu búsqueda.</p>
    </div>
    <div id="nx-curso-runner" hidden></div>`;

  const $picker = $el.querySelector('#nx-curso-picker');
  const $runner = $el.querySelector('#nx-curso-runner');
  const $buscar = $el.querySelector('#nx-curso-buscar');
  const $vacio = $el.querySelector('#nx-curso-vacio');

  // Busqueda: filtra tarjetas por nombre y oculta los encabezados de grupo que
  // quedan sin tarjetas visibles debajo.
  $buscar.addEventListener('input', () => {
    const q = $buscar.value.trim().toLowerCase();
    let visibles = 0;
    $el.querySelectorAll('.nx-curso-card').forEach(c => {
      const match = !q || (c.dataset.name || '').includes(q);
      c.style.display = match ? '' : 'none';
      if (match) visibles++;
    });
    // Encabezados: visibles solo si tienen alguna tarjeta visible por debajo antes del proximo encabezado.
    $el.querySelectorAll('.nx-curso-grupo').forEach(h => {
      let n = h.nextElementSibling, any = false;
      while (n && !n.hasAttribute('data-grupo')) {
        if (n.classList?.contains('nx-curso-card') && n.style.display !== 'none') { any = true; break; }
        n = n.nextElementSibling;
      }
      h.style.display = any ? '' : 'none';
    });
    $vacio.hidden = visibles > 0;
  });

  $el.querySelectorAll('.nx-curso-card').forEach(btn => {
    btn.addEventListener('click', () => {
      const topic = topics.find(t => t.code === btn.dataset.topic);
      $picker.hidden = true;
      $runner.hidden = false;
      runCurso($runner, topic, () => { $runner.hidden = true; $picker.hidden = false; $runner.innerHTML = ''; });
    });
  });
}

// Un curso: teoria (material_summary) arriba + el quiz del tema con feedback.
function runCurso($el, topic, onBack) {
  const material = topic.material_summary || topic.materialSummary || '';
  $el.innerHTML = `
    <div class="detail-section">
      <button type="button" id="nx-curso-back" class="btn-ghost btn-sm" style="margin-bottom:12px;cursor:pointer">&larr; Todos los cursos</button>
      <h2 style="margin-bottom:4px">${escHtml(topic.name)}</h2>
      ${material ? `<div style="background:#0d1117;border:1px solid #30363d;border-left:3px solid #238636;border-radius:8px;padding:12px 14px;margin:10px 0 16px;font-size:0.875rem;line-height:1.55;color:#c9d1d9;white-space:pre-wrap">${escHtml(material)}</div>` : ''}
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:10px">Ahora practica. Al responder verás si acertaste y <strong>por qué</strong>.</p>
      <div id="nx-curso-quiz-slot"></div>
    </div>`;
  $el.querySelector('#nx-curso-back')?.addEventListener('click', onBack);
  const slot = $el.querySelector('#nx-curso-quiz-slot');
  if (slot) {
    buildLearningEngine(slot, {
      userId: 'preview-admin',
      context: `curso:${topic.code}`,
      // _quizKey = codigo del tema (USO) o categoria del skill (GOV/SWE/MKT).
      category: topic._quizKey || topic.code,
      immediateFeedback: true
    });
  }
}

async function renderQuestionsTab($el) {
  $el.innerHTML = '<p class="empty-state">Cargando preguntas...</p>';

  const res = await LearningApiClient.getQuestions({ limit: 100, random: false });
  if (!res.ok) {
    $el.innerHTML = `<p class="empty-state">Error: ${res.error}</p>`;
    return;
  }

  const questions = (res.data?.data || []).map(q => ({
    ...q,
    question: q.question_text || q.questionText || '',
    opts: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
    correct: q.correct_index ?? q.correctIndex,
    cat: q.category
  }));

  const marketing = questions.filter(q => q.cat === 'marketing').length;
  const productividad = questions.filter(q => q.cat === 'productividad').length;

  const listConfig = {
    id: 'nx-cap-questions',
    columns: [
      { key: 'code', label: 'Codigo', width: '8%', render: v => `<code style="font-size:0.875rem">${escHtml(v)}</code>` },
      { key: 'question', label: 'Pregunta', render: v => `<span style="font-size:0.875rem">${escHtml(v?.substring(0, 80))}${v?.length > 80 ? '...' : ''}</span>` },
      { key: 'cat', label: 'Categoria', width: '12%', render: v => v === 'marketing'
        ? '<span class="task-tag" style="background:#f9758322;color:#f97583">marketing</span>'
        : '<span class="task-tag" style="background:#58a6ff22;color:#58a6ff">productividad</span>' },
      { key: 'difficulty', label: 'Dificultad', width: '8%', render: v => v === 1 ? 'basica' : v === 2 ? 'media' : 'avanzada' },
      { key: 'correct', label: 'Respuesta', width: '10%', render: (v, row) => {
        const opts = row.opts || [];
        return `<span style="font-size:0.875rem;color:#238636">${escHtml(opts[v]?.substring(0, 25) || '?')}...</span>`;
      }}
    ],
    data: questions,
    sortBy: 'code',
    sortDir: 'asc',
    emptyMessage: 'Sin preguntas en el banco'
  };

  $el.innerHTML = `
    <div class="banco-stats-grid" style="margin-bottom:12px">
      <div class="banco-stat-card">
        <div class="banco-stat-value">${questions.length}</div>
        <div class="banco-stat-label">Total preguntas</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#f97583">${marketing}</div>
        <div class="banco-stat-label">Marketing</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:#58a6ff">${productividad}</div>
        <div class="banco-stat-label">Productividad</div>
      </div>
    </div>
    <div class="detail-section">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <h2>Banco de Preguntas</h2>
        <button class="btn-primary btn-sm" id="nx-cap-add-q">+ Pregunta</button>
      </div>
      ${renderNxList(listConfig)}
    </div>`;

  bindNxListEvents($el, 'nx-cap-questions', listConfig, (row) => {
    showQuestionDetail($el, row);
  });

  // Add new question button handler
  const addBtn = $el.querySelector('#nx-cap-add-q');
  if (addBtn) {
    addBtn.onclick = () => showQuestionForm($el, null, () => renderQuestionsTab($el));
  }
}

function showQuestionDetail($parent, q) {
  let $detail = $parent.querySelector('#nx-cap-q-detail');
  if (!$detail) {
    $detail = document.createElement('div');
    $detail.id = 'nx-cap-q-detail';
    $detail.style.cssText = 'margin-top:12px;padding:14px;border:1px solid #21262d;border-radius:8px;background:#161b22';
    $parent.appendChild($detail);
  }
  $detail.hidden = false;

  const opts = q.opts || [];
  const catColor = q.cat === 'marketing' ? '#f97583' : q.cat === 'productividad' ? '#58a6ff' : '#238636';
  const skillMap = { marketing: 'SKILL-MKT-001/002', productividad: 'SKILL-PROD-001/002', nexo: 'SKILL-NEXO-001' };

  $detail.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:start">
      <div>
        <h3 style="margin:0;font-size:1rem">${escHtml(q.question)}</h3>
        <div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap">
          <span class="task-tag" style="background:${catColor}22;color:${catColor}">${escHtml(q.cat)}</span>
          <code style="font-size:0.875rem;color:#6e7681">${escHtml(q.code)}</code>
          <span style="font-size:0.875rem;color:#6e7681">Skill: ${skillMap[q.cat] || '?'}</span>
        </div>
      </div>
      <div style="display:flex;gap:6px">
        <button class="btn-ghost btn-sm" id="nx-cap-edit-q" style="font-size:0.875rem">Editar</button>
        <button class="btn-ghost btn-sm" onclick="this.closest('[id=nx-cap-q-detail]').hidden=true" style="font-size:0.875rem">&times;</button>
      </div>
    </div>
    <div style="margin-top:10px">
      <div style="font-size:0.875rem;color:#6e7681;margin-bottom:4px">Opciones:</div>
      ${opts.map((o, i) => `<div style="padding:4px 8px;font-size:0.875rem;border-left:3px solid ${i === q.correct ? '#238636' : '#21262d'};margin-bottom:4px;color:${i === q.correct ? '#238636' : '#c9d1d9'}">${i === q.correct ? '&#10003; ' : ''}${escHtml(o)}</div>`).join('')}
    </div>`;

  $detail.querySelector('#nx-cap-edit-q').onclick = () => {
    showQuestionForm($detail, q, () => { $detail.hidden = true; renderQuestionsTab($detail.closest('.detail-section')?.parentElement || $detail.parentElement); });
  };
  $detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function showQuestionForm($parent, existing, onSaved) {
  const isEdit = !!existing;
  let $form = $parent.querySelector('#nx-cap-q-form');
  if (!$form) {
    $form = document.createElement('div');
    $form.id = 'nx-cap-q-form';
    $form.style.cssText = 'margin-top:12px;padding:14px;border:1px solid #d29922;border-radius:8px;background:#161b22';
    $parent.appendChild($form);
  }

  const opts = existing?.opts || ['', '', '', ''];
  $form.innerHTML = `
    <h4 style="margin:0 0 8px">${isEdit ? 'Editar Pregunta' : 'Nueva Pregunta'}</h4>
    <textarea id="nxq-text" class="form-input" rows="2" placeholder="Texto de la pregunta">${escHtml(existing?.question || '')}</textarea>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:6px">
      ${opts.map((o, i) => `<input type="text" class="form-input" data-opt="${i}" value="${escHtml(o)}" placeholder="Opcion ${i + 1}" style="font-size:0.875rem">`).join('')}
    </div>
    <div style="display:flex;gap:8px;margin-top:6px;align-items:center">
      <select id="nxq-correct" class="form-input form-input-sm" style="width:auto">
        ${[0,1,2,3].map(i => `<option value="${i}"${i === (existing?.correct ?? 0) ? ' selected' : ''}>Correcta: ${i + 1}</option>`).join('')}
      </select>
      <select id="nxq-cat" class="form-input form-input-sm" style="width:auto">
        <option value="marketing"${existing?.cat === 'marketing' ? ' selected' : ''}>Marketing</option>
        <option value="productividad"${existing?.cat === 'productividad' ? ' selected' : ''}>Productividad</option>
        <option value="nexo"${existing?.cat === 'nexo' ? ' selected' : ''}>Nexo</option>
      </select>
      <button class="btn-primary btn-sm" id="nxq-save">${isEdit ? 'Guardar' : 'Crear'}</button>
      <button class="btn-ghost btn-sm" onclick="this.closest('[id=nx-cap-q-form]').remove()">Cancelar</button>
    </div>`;

  $form.querySelector('#nxq-save').onclick = async () => {
    const data = {
      questionText: $form.querySelector('#nxq-text').value.trim(),
      options: [...$form.querySelectorAll('[data-opt]')].map(el => el.value.trim()),
      correctIndex: parseInt($form.querySelector('#nxq-correct').value),
      category: $form.querySelector('#nxq-cat').value
    };
    if (!data.questionText || data.options.some(o => !o)) return;
    const res = isEdit
      ? await LearningApiClient.updateQuestion(existing.id, data)
      : await LearningApiClient.createQuestion(data);
    if (res.ok) { $form.remove(); if (onSaved) onSaved(); }
  };
  $form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function renderProgressTab($el) {
  $el.innerHTML = '<p class="empty-state">Cargando progreso...</p>';

  const res = await LearningApiClient.getProgressSummary();
  if (!res.ok) {
    $el.innerHTML = `<p class="empty-state">Error: ${res.error}</p>`;
    return;
  }

  const summary = res.data?.data || [];

  if (summary.length === 0) {
    $el.innerHTML = '<div class="detail-section"><h2>Progreso del Equipo</h2><p class="empty-state">Sin datos de progreso. Las respuestas se registran cuando los usuarios usan el quiz.</p></div>';
    return;
  }

  const listConfig = {
    id: 'nx-cap-progress',
    columns: [
      { key: 'user_id', label: 'Usuario', width: '25%', render: v => `<strong>${escHtml(v)}</strong>` },
      { key: 'total_answered', label: 'Respondidas', width: '15%' },
      { key: 'correct', label: 'Correctas', width: '15%', render: v => `<span style="color:#238636">${v}</span>` },
      { key: 'accuracy', label: 'Precision', width: '15%', render: v => {
        const pct = parseFloat(v) || 0;
        const color = pct >= 80 ? '#238636' : pct >= 50 ? '#d29922' : '#f85149';
        return `<span style="color:${color};font-weight:700">${pct}%</span>`;
      }}
    ],
    data: summary,
    sortBy: 'total_answered',
    sortDir: 'desc',
    emptyMessage: 'Sin datos'
  };

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Progreso del Equipo</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Metricas de aprendizaje registradas desde el quiz.</p>
      ${renderNxList(listConfig)}
    </div>`;

  bindNxListEvents($el, 'nx-cap-progress', listConfig);
}

async function renderPreviewTab($el) {
  $el.innerHTML = `
    <div class="detail-section">
      <h2>Vista Previa del Quiz</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Prueba el quiz como lo ven los usuarios. Respuestas se registran en la BD.</p>
      <div id="nx-cap-quiz-slot" style="margin-top:12px"></div>
    </div>`;

  const slot = $el.querySelector('#nx-cap-quiz-slot');
  if (slot) {
    await buildLearningEngine(slot, {
      userId: 'preview-admin',
      context: 'capacitacion-preview'
    });
  }
}

async function renderFlashCardsTab($el) {
  $el.innerHTML = `
    <div class="detail-section">
      <h2>Tarjetas de Memoria</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Voltea la tarjeta para ver la respuesta. Marca si sabias o no. Progreso se registra en la BD.</p>
      <div id="nx-cap-fc-slot" style="margin-top:12px"></div>
    </div>`;

  const slot = $el.querySelector('#nx-cap-fc-slot');
  if (slot) {
    await buildFlashCards(slot, {
      userId: 'preview-admin',
      context: 'flashcards-preview'
    });
  }
}
